import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, TriangleAlert as AlertTriangle, RefreshCw, Trash2, CheckCheck } from 'lucide-react';
import {
  NOTIFICATIONS_URL,
  NOTIFICATIONS_UNREAD_COUNT_URL,
  NOTIFICATION_MARK_READ_URL,
  NOTIFICATIONS_MARK_ALL_READ_URL,
  NOTIFICATION_DELETE_URL,
  NOTIFICATIONS_DELETE_ALL_URL,
  NOTIFICATIONS_STREAM_URL,
} from '../../config/api';
import { useOrderSocket, type OrderStatusEvent } from '../Utils/useOrderSocket';
import { toDate } from '../Utils/time';
import './NotificationBell.css';

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Order confirmed',
  preparing: 'Kitchen is preparing your order',
  ready: 'Order ready for pickup/delivery',
  out_for_delivery: 'Order out for delivery',
  delivered: 'Order delivered',
  cancelled: 'Order cancelled',
};

function ephemeralId(orderId: number, status: string): number {
  return parseInt(`${orderId}${String(status).charCodeAt(0)}${String(status).length}`, 10);
}

function statusKey(orderId: number, status: string): string {
  return `${orderId}:${status}`;
}

interface Notification {
  notification_id: number;
  title?: string;
  body: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationsResponse {
  success: boolean;
  total: number;
  notifications: Notification[];
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - toDate(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return toDate(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

const FALLBACK_POLL_INTERVAL = 30000;
const SSE_RECONNECT_DELAY = 5000;

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [markingId, setMarkingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const sseAbortRef = useRef<AbortController | null>(null);
  const sseActiveRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenStatusKeysRef = useRef<Set<string>>(new Set());

  const getToken = () => localStorage.getItem('authToken') || '';

  const fetchUnreadCount = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(NOTIFICATIONS_UNREAD_COUNT_URL, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        const count =
          typeof data === 'number'
            ? data
            : typeof data?.unread_count === 'number'
            ? data.unread_count
            : typeof data?.count === 'number'
            ? data.count
            : 0;
        setUnreadCount(count);
      }
    } catch {
      // badge is informational — fail silently
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setListLoading(true);
    setListError('');
    try {
      const res = await fetch(NOTIFICATIONS_URL, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.ok) {
        const data: NotificationsResponse = await res.json();
        const list = data.notifications || [];
        setNotifications(list);
        setTotal(data.total ?? list.length);
        setUnreadCount(list.filter(n => !n.is_read).length);
      } else {
        setListError('Failed to load notifications.');
      }
    } catch {
      setListError('Network error. Please try again.');
    } finally {
      setListLoading(false);
    }
  }, []);

  const startSSE = useCallback(() => {
    const token = getToken();
    if (!token || sseActiveRef.current) return;

    const controller = new AbortController();
    sseAbortRef.current = controller;
    sseActiveRef.current = true;

    (async () => {
      try {
        const res = await fetch(NOTIFICATIONS_STREAM_URL, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });

        if (!res.ok || !res.body) throw new Error('SSE connection failed');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const raw = line.slice(5).trim();
            if (!raw || raw === 'ping') continue;
            try {
              const event = JSON.parse(raw);
              if (typeof event?.unread_count === 'number') {
                setUnreadCount(event.unread_count);
              }
              if (event?.notification) {
                const n: Notification = event.notification;
                const bodyLower = (n.body || '').toLowerCase();
                const orderMatch = bodyLower.match(/order\s*#?(\d+)/);
                if (orderMatch) {
                  const orderId = parseInt(orderMatch[1], 10);
                  for (const [statusKeyVal] of Object.entries(STATUS_LABELS)) {
                    if (seenStatusKeysRef.current.has(statusKey(orderId, statusKeyVal))) {
                      const words = statusKeyVal.replace(/_/g, ' ').split(' ');
                      const matches = words.some(w => w.length > 3 && bodyLower.includes(w));
                      if (matches) return;
                    }
                  }
                }
                setNotifications(prev => {
                  const exists = prev.some(x => x.notification_id === n.notification_id);
                  return exists ? prev : [n, ...prev];
                });
                setTotal(t => t + 1);
              }
            } catch {
              // malformed SSE data — skip
            }
          }
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      } finally {
        sseActiveRef.current = false;
      }

      // Reconnect after delay if stream ended unexpectedly
      if (!controller.signal.aborted) {
        reconnectTimerRef.current = setTimeout(startSSE, SSE_RECONNECT_DELAY);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopSSE = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    sseAbortRef.current?.abort();
    sseAbortRef.current = null;
    sseActiveRef.current = false;
  }, []);

  const handleOrderStatus = useCallback((event: OrderStatusEvent) => {
    const status = (event.status || '').toLowerCase().trim();
    if (status === 'on_the_way') return;
    const key = statusKey(event.order_id, status);
    if (seenStatusKeysRef.current.has(key)) return;
    seenStatusKeysRef.current.add(key);

    const label = STATUS_LABELS[status] ?? `Order status: ${status}`;
    const n: Notification = {
      notification_id: ephemeralId(event.order_id, status),
      title: `Order #${event.order_id} Update`,
      body: label,
      is_read: false,
      created_at: event.updated_at || new Date().toISOString(),
    };
    setNotifications(prev => {
      const exists = prev.some(x => x.notification_id === n.notification_id);
      return exists ? prev : [n, ...prev];
    });
    setUnreadCount(c => c + 1);
    setTotal(t => t + 1);
  }, []);

  useOrderSocket({ onOrderStatus: handleOrderStatus });

  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchUnreadCount();
      startSSE();
    }

    const onSession = () => stopSSE();
    window.addEventListener('sessionExpired', onSession);

    return () => {
      stopSSE();
      window.removeEventListener('sessionExpired', onSession);
    };
  }, [fetchUnreadCount, startSSE, stopSSE]);

  // Fallback polling to keep badge in sync
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, FALLBACK_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAsRead = useCallback(async (id: number) => {
    const token = getToken();
    if (!token || markingId !== null) return;
    setMarkingId(id);
    try {
      const res = await fetch(NOTIFICATION_MARK_READ_URL(id), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      // fail silently
    } finally {
      setMarkingId(null);
    }
  }, [markingId]);

  const markAllRead = useCallback(async () => {
    const token = getToken();
    if (!token || bulkLoading) return;
    setBulkLoading(true);
    try {
      const res = await fetch(NOTIFICATIONS_MARK_ALL_READ_URL, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch {
      // fail silently
    } finally {
      setBulkLoading(false);
    }
  }, [bulkLoading]);

  const deleteNotification = useCallback(async (id: number) => {
    const token = getToken();
    if (!token || deletingId !== null) return;
    setDeletingId(id);
    try {
      const res = await fetch(NOTIFICATION_DELETE_URL(id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.ok) {
        setNotifications(prev => {
          const removed = prev.find(n => n.notification_id === id);
          if (removed && !removed.is_read) setUnreadCount(c => Math.max(0, c - 1));
          return prev.filter(n => n.notification_id !== id);
        });
        setTotal(t => Math.max(0, t - 1));
      }
    } catch {
      // fail silently
    } finally {
      setDeletingId(null);
    }
  }, [deletingId]);

  const deleteAll = useCallback(async () => {
    const token = getToken();
    if (!token || bulkLoading) return;
    setBulkLoading(true);
    try {
      const res = await fetch(NOTIFICATIONS_DELETE_ALL_URL, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (res.ok) {
        setNotifications([]);
        setUnreadCount(0);
        setTotal(0);
      }
    } catch {
      // fail silently
    } finally {
      setBulkLoading(false);
    }
  }, [bulkLoading]);

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const hasUnread = notifications.some(n => !n.is_read);

  return (
    <div className="nb-root">
      <button
        ref={buttonRef}
        className={`nb-bell-btn${open ? ' nb-bell-btn--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="nb-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="nb-panel">
          <div className="nb-panel-header">
            <span className="nb-panel-title">
              Notifications{total > 0 ? ` (${total})` : ''}
            </span>
            <div className="nb-panel-actions">
              {hasUnread && (
                <button
                  className="nb-icon-btn nb-icon-btn--label"
                  onClick={markAllRead}
                  disabled={bulkLoading}
                  title="Mark all as read"
                >
                  <CheckCheck size={13} />
                  <span>All read</span>
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="nb-icon-btn nb-icon-btn--danger"
                  onClick={deleteAll}
                  disabled={bulkLoading}
                  title="Clear all notifications"
                >
                  <Trash2 size={14} />
                </button>
              )}
              <button
                className="nb-icon-btn"
                onClick={fetchNotifications}
                disabled={listLoading}
                title="Refresh"
              >
                <RefreshCw size={14} className={listLoading ? 'nb-spinning' : ''} />
              </button>
              <button className="nb-icon-btn" onClick={() => setOpen(false)} title="Close">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="nb-panel-body">
            {listLoading && (
              <div className="nb-state nb-state--loading">
                <RefreshCw size={20} className="nb-spinning" />
                <span>Loading...</span>
              </div>
            )}

            {listError && !listLoading && (
              <div className="nb-state nb-state--error">
                <AlertTriangle size={16} />
                <span>{listError}</span>
              </div>
            )}

            {!listLoading && !listError && notifications.length === 0 && (
              <div className="nb-state nb-state--empty">
                <Bell size={32} />
                <span>You have no notifications</span>
              </div>
            )}

            {!listLoading && !listError && notifications.length > 0 && (
              <ul className="nb-list">
                {notifications.map(n => {
                  const expanded = expandedIds.has(n.notification_id);
                  const isLong = n.body.length > 180;
                  const displayBody = isLong && !expanded ? n.body.slice(0, 180) + '…' : n.body;

                  return (
                    <li key={n.notification_id} className={`nb-item${n.is_read ? ' nb-item--read' : ''}`}>
                      {!n.is_read && <span className="nb-unread-dot" aria-hidden="true" />}
                      <div className="nb-item-body">
                        {n.title && <p className="nb-item-title">{n.title}</p>}
                        <p className="nb-item-message">{displayBody}</p>
                        {isLong && (
                          <button
                            className="nb-expand-btn"
                            onClick={() => toggleExpand(n.notification_id)}
                          >
                            {expanded ? 'Show less' : 'Show more'}
                          </button>
                        )}
                        <span className="nb-item-time">{timeAgo(n.created_at)}</span>
                      </div>
                      <div className="nb-item-actions">
                        {!n.is_read && (
                          <button
                            className="nb-action-btn nb-action-btn--read"
                            onClick={() => markAsRead(n.notification_id)}
                            disabled={markingId === n.notification_id || bulkLoading}
                            title="Mark as read"
                          >
                            {markingId === n.notification_id ? (
                              <RefreshCw size={12} className="nb-spinning" />
                            ) : (
                              <Check size={12} />
                            )}
                          </button>
                        )}
                        <button
                          className="nb-action-btn nb-action-btn--delete"
                          onClick={() => deleteNotification(n.notification_id)}
                          disabled={deletingId === n.notification_id || bulkLoading}
                          title="Delete"
                        >
                          {deletingId === n.notification_id ? (
                            <RefreshCw size={12} className="nb-spinning" />
                          ) : (
                            <Trash2 size={12} />
                          )}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
